import { Intro, Text } from "components/commons";
import { StationContext } from "contexts";
import { useApi, useExport, useModal } from "hooks";
import React, { useCallback, useContext } from "react";
import PaymentHistoryFilter from "./payment-history-filter";
import { paymentHistoryState } from "./payment-history-filter.state";
import locale from "localization";
import { SettlementStatus } from "enums";
import { updateRedemptionRemarks, voidRedemption } from "apis/redemption.api";
import { useState } from "react";
import { prettifyVoidTransactionValue } from "utils/pretty.utils";
import { formatVolume } from "utils";
import VoidTransactionModal from "../payment-history-business/void-transaction-modal";
import ConfirmModal from "components/modals/confirm-modal/confirm-modal";
import SuccessModal from "components/modals/success-modal/success-modal";
import { getRedemptions, generateStationReport } from "apis";
import ReceiptGuideModal from "./receipt-guide-modal";
import useDataTable from "hooks/useDataTable";
import DataTableV2 from "components/commons/data-table/data-table-v2";
import { mapDataToList, mapFilterToRequest } from "./payment-history.mapper";

const PaymentHistoryModule = ({ title, columns }) => {
  const [remarks, setRemarks] = useState();
  const [value, setValue] = useState("");

  const { station } = useContext(StationContext);
  const { stationId } = station;

  const { request: voidRequest, loading: voidingRequest } = useApi({
    api: voidRedemption,
    modalError: true,
    pageError: false,
  });

  const updateRemarksApi = useApi({
    api: updateRedemptionRemarks,
    modalError: true,
    pageError: false,
  });

  const voidTransactionModal = useModal();
  const receiptGuideModal = useModal();
  const confirmModal = useModal();

  const handleVoidPayment = ({ redemptions }) => {
    setRemarks("");
    voidTransactionModal.show({
      redemptionItem: redemptions,
      title: `${locale.voidTransaction}?`,
      description: (
        <Text align="center">
          <locale.Populate
            text={
              redemptions?.redemption?.settlementId
                ? redemptions?.redemption?.settlementStatus === SettlementStatus.ForProcessing
                  ? locale.voidingThisTransactionSettlementProcessing
                  : locale.voidingThisTransactionSettlementProcessed
                : locale.voidingThisTransaction
            }
            items={[<b>{formatVolume(redemptions?.redemption?.literVolume)}</b>]}
          />
        </Text>
      ),
      label: `${locale.reason}/${locale.remarks}`,
      actionText: locale.voidTransaction,
      submit: (value) => {
        onVoidTransactionCb({
          redemptionItem: redemptions,
          value,
        });
      },
    });
  };

  const handleEdit = ({ redemptions }) => {
    setRemarks(redemptions?.redemption?.remarks);
    setValue(prettifyVoidTransactionValue(redemptions?.redemption?.remarks));
    voidTransactionModal.show({
      redemptionItem: redemptions,
      title: locale.editRemarks,
      description: null,
      label: `${locale.reason}/${locale.remarks}`,
      actionText: locale.save,
      submit: async (value) => {
        onHandleEdit({
          redemptionItem: redemptions,
          value,
        });
      },
    });
  };

  const handleReceiptGuide = ({ redemptions }) => {
    setRemarks("");
    receiptGuideModal.show({
      redemptionItem: redemptions,
      title: `${locale.receiptGuide}`,
      description: "",
      label: `${locale.reason}/${locale.remarks}`,
    });
  };

  const { filter, search, table } = useDataTable({
    api: { api: getRedemptions, params: { stationId, platformType: "plc" } },
    filter: { initialState: paymentHistoryState(), mapper: mapFilterToRequest },
    table: {
      key: "redemptions",
      mapper: (redemptions) =>
        mapDataToList({ redemptions, handleVoidPayment, handleReceiptGuide, handleEdit }),
      columns,
    },
  });

  const exports = useExport({
    api: generateStationReport,
    reportType: "plc-redemption",
    hasModal: true,
    mappedFilterState: {
      ...filter?.mappedFilterState,
      platformType: "plc",
      redemptionStationId: stationId,
      searchKey: search?.searchKey,
    },
  });

  const onVoidTransactionCb = useCallback(
    async ({ redemptionItem, value }) => {
      await voidRequest({ redemptionId: redemptionItem.redemptionId, remarks: value });
      voidTransactionModal.close();
      table.fetch({});
      confirmModal.show({
        title: locale.transactionVoided,
        content: (
          <locale.Populate
            text={locale.thisTransactionHasBeenVoided}
            items={[
              <b>{formatVolume(redemptionItem?.redemption?.literVolume)}</b>,
              <b>{redemptionItem?.fleet?.businessName}</b>,
            ]}
          />
        ),
        loading: voidingRequest,
        redemptionItem,
      });
      setRemarks("");
    },
    [voidRequest, voidTransactionModal, voidingRequest, confirmModal, table]
  );

  const onHandleEdit = useCallback(
    async ({ redemptionItem, value }) => {
      await updateRemarksApi.request({
        id: redemptionItem?.redemptionId,
        remarks: value,
      });
      voidTransactionModal.close();
      setRemarks("");
      table.fetch({});
    },
    [updateRemarksApi, voidTransactionModal, table]
  );

  return (
    <div>
      <VoidTransactionModal
        {...voidTransactionModal}
        remarks={remarks}
        onChangeRemarks={(v) => {
          setRemarks(v);
        }}
        loading={voidingRequest || updateRemarksApi.loading}
        onSubmit={voidTransactionModal.submit}
        value={value}
        setValue={setValue}
      />
      <ReceiptGuideModal
        {...receiptGuideModal}
        remarks={remarks}
        value={value}
        setValue={setValue}
      />
      <ConfirmModal {...confirmModal} />
      <SuccessModal {...exports?.successModalComponent} />
      <div>
        <Intro title={locale.populate(locale.paymentHistoryTitle, [title])} />
      </div>
      <PaymentHistoryFilter {...filter} {...search} {...exports} />
      <DataTableV2 {...table} paginationV2={true} />
    </div>
  );
};

export default PaymentHistoryModule;
